import { useEffect, useMemo, useState } from 'react'

import { FreshWeighingList } from './-my-weighing-lists'
import type { FilterSearchParams } from '@/components/common/filters/types'
import { PageBackButton } from '@/components/common/PageBackButton'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { defineListFilters } from '@/components/common/filters/core'
import { dateFilter } from '@/components/common/filters/date-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useMyGrowers } from '@/hooks/use-care-logs-query'
import { useTeaPurchasesList } from '@/hooks/use-tea-purchase-query'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/**
 * "Số cân của tôi" — màn CHỈ ĐỌC cho tài khoản hộ trồng (quyền
 * `tea_purchases.read_own`). Chỉ phiếu cân chè TƯƠI: đó là con số hộ quan tâm
 * (kg họ giao cho HTX). Kg khô là số nội bộ của khâu ra lò nên không đưa vào
 * đây, và role hộ cũng không được mở endpoint phiếu khô.
 *
 * Màn luôn xem ĐÚNG MỘT hộ (mặc định hộ đầu tiên gắn với tài khoản); ô chọn hộ
 * chỉ hiện khi tài khoản gắn từ hai hộ trở lên. Server còn ép phạm vi thêm một
 * lớp cho tài khoản chỉ có `read_own`.
 */

export const MY_WEIGHINGS_FILTERS = defineListFilters({
    date_from: dateFilter({ label: m.my_weighings_date_from }),
    date_to: dateFilter({ label: m.my_weighings_date_to }),
})

/** Trần một lần tải. Vượt trần thì báo rõ thay vì lặng lẽ cắt bớt. */
const PER_PAGE = 100

interface MyWeighingsScreenProps {
    search: Record<string, unknown>
    onSearchChange: (
        next: FilterSearchParams,
        opts?: { replace?: boolean },
    ) => void
}

export function MyWeighingsScreen({
    search,
    onSearchChange,
}: MyWeighingsScreenProps) {
    const { values, barProps } = useListFilters({
        def: MY_WEIGHINGS_FILTERS,
        search,
        onSearchChange,
    })

    const { data: growers, isLoading: growersLoading } = useMyGrowers(true)
    const linked = growers ?? []
    // Màn này chỉ có nghĩa với tài khoản đã gắn hộ. Chưa gắn hộ nào thì không
    // gọi API: tài khoản quản trị (tea_purchases.read) mở URL này bằng tay sẽ
    // không kéo về phiếu của toàn bộ hộ trên một màn mang tên "của tôi".
    const hasGrower = linked.length > 0

    // Luôn xem ĐÚNG MỘT hộ, không có lựa chọn "tất cả": tài khoản vừa là nhân
    // viên (tea_purchases.read) vừa gắn hộ thì server không ép phạm vi, nên
    // thiếu grower_id là màn "của tôi" sẽ kéo về phiếu của mọi hộ trong HTX.
    const [growerId, setGrowerId] = useState('')
    // Hộ đang chọn không còn được gắn (bị gỡ liên kết / xoá) thì rơi về hộ đầu
    // tiên, tránh kẹt bộ lọc cũ khi ô chọn hộ bị ẩn.
    const selected = linked.some((g) => g.id === growerId) ? growerId : ''
    const effectiveGrowerID = selected || (linked[0]?.id ?? '')
    useEffect(() => {
        if (effectiveGrowerID && effectiveGrowerID !== growerId) {
            setGrowerId(effectiveGrowerID)
        }
    }, [effectiveGrowerID, growerId])

    const params = useMemo(
        () => ({
            page: 1,
            per_page: PER_PAGE,
            grower_id: effectiveGrowerID,
            ...(values.date_from ? { date_from: values.date_from } : {}),
            ...(values.date_to ? { date_to: values.date_to } : {}),
        }),
        [effectiveGrowerID, values.date_from, values.date_to],
    )

    const fresh = useTeaPurchasesList(params, {
        enabled: hasGrower && !!effectiveGrowerID,
    })
    const items = fresh.data?.data ?? []
    // `total` là tổng toàn bộ kết quả, `items` chỉ là trang đầu (trần PER_PAGE).
    // Câu tổng phải đếm theo đúng số phiếu đã cộng kg, nếu không hộ đọc ra
    // "137 phiếu · 1.000 kg" trong khi 1.000 kg mới là của 100 phiếu.
    const total = fresh.data?.meta?.total ?? items.length
    const sumKg = items.reduce((acc, row) => acc + (row.quantity ?? 0), 0)

    return (
        <div className="space-y-4 page-pad md:space-y-6">
            <div className="flex min-w-0 items-start gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <header className="space-y-1">
                    <h1 className="text-xl font-semibold md:text-2xl">
                        {m.my_weighings_title()}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {m.my_weighings_subtitle()}
                    </p>
                </header>
            </div>

            {!growersLoading && !hasGrower ? (
                <p className="rounded-2xl border bg-card p-5 text-sm text-muted-foreground">
                    {m.my_weighings_no_grower()}
                </p>
            ) : null}

            {linked.length > 1 ? (
                <div className="space-y-1.5">
                    <Label htmlFor="my-weighings-grower">
                        {m.my_weighings_grower_label()}
                    </Label>
                    <Select
                        value={effectiveGrowerID}
                        onValueChange={setGrowerId}
                    >
                        <SelectTrigger
                            id="my-weighings-grower"
                            className="h-11 w-full md:h-9 md:max-w-xs"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {linked.map((g) => (
                                <SelectItem key={g.id} value={g.id}>
                                    {g.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            ) : null}

            {hasGrower ? (
                <>
                    <ListFilterBar {...barProps} />

                    <p className="text-sm font-medium">
                        {m.my_weighings_total_fresh({
                            count: items.length,
                            kg: formatQty(sumKg),
                        })}
                    </p>
                    {total > items.length ? (
                        <p className="text-xs text-muted-foreground">
                            {m.my_weighings_partial({
                                shown: items.length,
                                total,
                            })}
                        </p>
                    ) : null}

                    <WeighingBody
                        isLoading={fresh.isLoading}
                        isError={fresh.isError}
                        onRetry={() => void fresh.refetch()}
                        isEmpty={items.length === 0}
                    >
                        <FreshWeighingList items={items} />
                    </WeighingBody>
                </>
            ) : null}
        </div>
    )
}

/** Bọc 3 trạng thái đang tải / lỗi / rỗng của danh sách. */
function WeighingBody({
    isLoading,
    isError,
    onRetry,
    isEmpty,
    children,
}: {
    isLoading: boolean
    isError: boolean
    onRetry: () => void
    isEmpty: boolean
    children: React.ReactNode
}) {
    if (isError) {
        return (
            <div className="space-y-3">
                <p className="rounded-2xl border bg-card p-5 text-sm text-muted-foreground">
                    {m.my_weighings_load_error()}
                </p>
                <Button type="button" onClick={onRetry}>
                    {m.my_weighings_retry()}
                </Button>
            </div>
        )
    }
    if (isLoading) {
        return (
            <p className="py-8 text-center text-muted-foreground">
                {m.common_loading()}
            </p>
        )
    }
    if (isEmpty) {
        return (
            <p className="py-8 text-center text-muted-foreground">
                {m.my_weighings_empty_fresh()}
            </p>
        )
    }
    return <>{children}</>
}
